import { useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import { BottomSheet } from '@/components/BottomSheet/BottomSheet';
import { ANKETA_TYPE_CARDS } from '../../anketaTypeCards';

/**
 * Block 12 Phase D2 Step 3 + PR-H7 (ADR-0030) — bottom-sheet выбора типа мебели.
 *
 * 8 preset options из `ANKETA_TYPE_CARDS` (Calculator entry single source of
 * truth) + custom string input. До PR-H7 picker iterировал `FURNITURE_TYPES`
 * (10 legacy labels через wrapper) — теперь использует `ANKETA_TYPE_CARDS`
 * напрямую, синхронизированный с Calculator entry screen 2.12.1.
 *
 * Прототип `docs/prototypes/screen-2.12.10-cp.html` lines 377-441 (структура
 * sheet preserved — изменился только список preset кнопок).
 *
 * Reuses existing `<BottomSheet>` primitive (Block 12 Phase B1) — backdrop +
 * escape + body scroll lock + close button уже встроены. Эта обёртка добавляет
 * только domain-специфичный body (список + custom input + apply).
 *
 * **Контракт:**
 *  - `value` — текущий выбранный тип (для подсветки aria-pressed + Check icon)
 *  - `onChange(next)` — callback с новой строкой (preset либо custom)
 *  - `onClose()` — закрытие без выбора (родитель обнуляет open state)
 *  - sheet сам очищает локальный custom input при open=false → open=true
 *
 * References:
 *   - ADR-0030 §5 (renders ANKETA_TYPE_CARDS labels)
 *   - PRD `docs/prd/v4/10-block-12-calculator.md` §10.12.10 «Блок Тип мебели»
 */
export interface FurnitureTypePickerProps {
  isOpen: boolean;
  value: string | null;
  onChange: (next: string) => void;
  onClose: () => void;
}

export function FurnitureTypePicker({
  isOpen,
  value,
  onChange,
  onClose,
}: FurnitureTypePickerProps) {
  const [customDraft, setCustomDraft] = useState('');

  // Reset custom input each time sheet opens — иначе старый текст «висит».
  useEffect(() => {
    if (isOpen) setCustomDraft('');
  }, [isOpen]);

  const handlePickPreset = (option: string) => {
    onChange(option);
    onClose();
  };

  const trimmed = customDraft.trim();
  const handleApplyCustom = () => {
    if (!trimmed) return;
    onChange(trimmed);
    onClose();
  };

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title="Выбери тип мебели или укажи свой"
      maxHeightVh={85}
    >
      <p className="text-muted text-sm mb-3">
        Используется в имени сохранённого КП и для синхронизации с калькулятором.
      </p>

      <ul className="flex flex-col gap-2 mb-4" role="list">
        {ANKETA_TYPE_CARDS.map((card) => {
          const active = value === card.label;
          return (
            <li key={card.anketaType}>
              <button
                type="button"
                aria-pressed={active}
                onClick={() => handlePickPreset(card.label)}
                className={`w-full min-h-14 px-4 rounded-xl border-[1.5px] flex items-center justify-between text-base font-semibold text-left focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none ${
                  active ? 'bg-ink text-card border-ink' : 'bg-card text-ink border-accent'
                }`}
              >
                <span>{card.label}</span>
                {active ? <Check size={20} aria-hidden="true" /> : null}
              </button>
            </li>
          );
        })}
      </ul>

      <div className="border-t border-muted/30 pt-4 mb-4">
        <label className="text-ink text-sm font-semibold block mb-2" htmlFor="quote-custom-type">
          Свой вариант
        </label>
        <input
          id="quote-custom-type"
          type="text"
          value={customDraft}
          onChange={(e) => setCustomDraft(e.target.value)}
          placeholder="Например: Кухонный гарнитур, Шкаф под лестницу"
          aria-label="Свой тип мебели"
          className="w-full min-h-12 px-3 border-[1.5px] border-accent rounded-xl text-base text-ink bg-card focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none"
        />
        <button
          type="button"
          onClick={handleApplyCustom}
          disabled={!trimmed}
          className="w-full min-h-12 mt-3 rounded-xl bg-cta text-ink text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none"
        >
          Применить свой вариант
        </button>
      </div>

      <button
        type="button"
        aria-label="Закрыть окно выбора типа мебели"
        onClick={onClose}
        className="w-full min-h-12 rounded-xl border-[1.5px] border-accent bg-card text-ink text-base font-medium focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none"
      >
        Закрыть
      </button>
    </BottomSheet>
  );
}
